import { tv } from "@ui/lib/tv";
import type { ReactNode } from "react";

const cardGrid = tv({
  base: "grid",
  variants: {
    // Default fill: min track 16rem (`w-3xs`), tracks expand to `1fr` so cards distribute
    // the row evenly. Container-driven sizing so cards in a CardCarousel and a CardGrid
    // on the same page land at matching widths.
    //
    // `capCardWidth`: lowers the min track to 14rem so auto-fill packs more tracks per
    // row, and tracks still expand to `1fr` so each row fills evenly. The effect: cards
    // size as if "one more card could fit" — a 2-card row in an ~820px container lands at
    // ~252px each (3-fit width) rather than expanding to ~394px (no cap) or sitting at
    // ~288px with leftover (hard cap). Use on surfaces where the container is moderately
    // narrow and you want compact tiles (profile store sections; sidebar-narrowed regions).
    capCardWidth: {
      true: "grid-cols-[repeat(auto-fill,minmax(min(100%,14rem),1fr))]",
      false: "grid-cols-[repeat(auto-fill,minmax(min(100%,16rem),1fr))]",
    },
    gap: {
      md: "gap-4 md:gap-6",
      lg: "gap-6 md:gap-8",
      xl: "gap-8 md:gap-10",
    },
  },
  defaultVariants: { capCardWidth: false, gap: "lg" },
});

export interface CardGridProps {
  children: ReactNode;
  /** When true, cards stay compact ("3-fit width") instead of expanding to fill the row.
   *  Implemented by lowering the min track from 16rem to 14rem so auto-fill packs more
   *  tracks per row; tracks then distribute evenly via `1fr`. The effect: a 2-card row
   *  in an ~820px container lands at ~252px each (the size 3 cards would land at) rather
   *  than ballooning to ~394px each. Use on surfaces where the container is moderately
   *  narrow and you want compact tiles (profile store sections; sidebar-narrowed regions).
   *  Default false. */
  capCardWidth?: boolean;
  /** Inter-card gap. Default `lg` (24px → 32px md+). */
  gap?: "md" | "lg" | "xl";
  className?: string;
}

/** Intrinsic responsive grid for cards. Min track size 16rem (`w-3xs`) drops a column
 *  when the row can't fit; tracks expand to `1fr` (default) so cards distribute the row
 *  evenly. `min(100%, 16rem)` lets a single column shrink below the minimum to fit a
 *  container narrower than 16rem.
 *
 *  For surfaces that want a horizontally-scrolling row of cards (always single-row, with
 *  arrows on wide containers) — use `CardCarousel` instead. */
export function CardGrid({ capCardWidth, children, className, gap }: CardGridProps) {
  return <div className={cardGrid({ capCardWidth, gap, className })}>{children}</div>;
}
